import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Clock } from 'lucide-react';
import { getPostBySlug, BLOG_POSTS } from './posts';
import IndianBreakfastNearMe from './content/IndianBreakfastNearMe';
import BestBiryaniOmaha from './content/BestBiryaniOmaha';
import OrderBiryaniDelivery from './content/OrderBiryaniDelivery';

// Map slug -> article body component. Add new posts here as they're written.
const CONTENT: Record<string, React.ComponentType> = {
  'indian-breakfast-near-me': IndianBreakfastNearMe,
  'best-biryani-in-omaha': BestBiryaniOmaha,
  'order-biryani-delivery-takeout-omaha': OrderBiryaniDelivery,
};

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post || !slug || !CONTENT[slug]) {
    return <Navigate to="/blog" replace />;
  }

  const Body = CONTENT[slug];
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);
  const canonicalPath = `/blog/${post.slug}`;

  return (
    <>
      <Helmet>
        <title>{post.title} — Biryani2Go Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://biryani2go.us/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://biryani2go.us/blog/${post.slug}`} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: post.excerpt,
            image: [post.image],
            datePublished: post.date,
            dateModified: post.date,
            author: { '@type': 'Organization', name: 'Biryani2Go' },
            publisher: { '@type': 'Organization', name: 'Biryani2Go', logo: { '@type': 'ImageObject', url: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/485ee0f5-162d-499f-b44f-69bc80fbabf9/1785625914831-6ytm3p.png' } },
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://biryani2go.us/blog/${post.slug}` },
            wordCount: 1200,
            articleBody: post.excerpt,
          }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://biryani2go.us' },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://biryani2go.us/blog' },
              { '@type': 'ListItem', position: 3, name: post.title, item: `https://biryani2go.us/blog/${post.slug}` },
            ],
          }) }} />
      </Helmet>

      <section className="relative bg-maroon py-16 md:py-24 overflow-hidden">
        <img src={post.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-maroon to-maroon/50" />
        <div className="relative max-w-screen-md mx-auto px-5 md:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-marigold hover:gap-3 transition-all mb-6">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="eyebrow text-marigold mb-4">
            {post.category}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="font-display font-semibold text-3xl md:text-5xl leading-tight tracking-[-0.01em] text-white"
          >
            {post.title}
          </motion.h1>
          <div className="mt-6 flex items-center gap-5 text-sm text-[#D9BFA6]">
            <span className="flex items-center gap-1.5">
              <Calendar size={15} />
              {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={15} />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      <article className="bg-surface py-14 md:py-20">
        <div className="max-w-screen-md mx-auto px-5 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="prose-article"
          >
            <Body />
          </motion.div>

          <div className="mt-14 pt-10 border-t border-line flex flex-wrap gap-4">
            <Link to="/menu" className="btn-primary">
              Order Breakfast Now <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn-secondary">
              Ask Us a Question
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-surface2 py-14 md:py-20">
          <div className="max-w-screen-xl mx-auto px-5 md:px-8">
            <h2 className="font-display font-semibold text-2xl md:text-3xl text-maroon mb-8">More from the Blog</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="card-tin overflow-hidden flex gap-4 p-4 items-center group">
                  <img src={p.image} alt={p.title} className="w-24 h-24 rounded-xl object-cover shrink-0" />
                  <div>
                    <h3 className="font-display font-semibold text-maroon leading-snug group-hover:text-ember transition-colors">{p.title}</h3>
                    <p className="text-xs text-ink-2 mt-1">{p.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
